/**
 * Extended CAD Drawing Tools
 * 
 * Additional 2D drawing tools for CAD:
 * - Arc (3-point, center-start-end)
 * - Polygon (regular n-sided)
 * - Polyline (connected segments)
 * - Ellipse
 * - Spline
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore } from '../../state/store';
import { GROUND_PLANE } from '../../core/scene';
import { constrainDraftPoint } from '../snapping';
import { undoableAdd, makeEntity } from '../../core/commands/entityOps';
import { bulgeFrom3Points } from '../../core/commands/geometryMath';

// ============================================================================
// ARC TOOL
// ============================================================================

export interface ArcData {
    center: number[];
    radius: number;
    startAngle: number;
    endAngle: number;
}

/**
 * Arc Tool Hook - 3-point arc drawing
 * 
 * Click 1: Start point
 * Click 2: End point  
 * Click 3: Point on arc (defines curvature)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useArcTool() {
    const [points, setPoints] = useState<THREE.Vector3[]>([]);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);
    const [arcPreview, setArcPreview] = useState<ArcData | null>(null);
    // Center-Start-End mode (C key): first click is the arc center.
    const [cseMode, setCseMode] = useState(false);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'arc';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3, base?: THREE.Vector3 | null): THREE.Vector3 => {
        return constrainDraftPoint(point, camera, gl, base);
    }, [camera, gl]);

    /**
     * Calculate arc from 3 points: p1 = start, p2 = point on arc, p3 = end.
     * The stored span runs CCW from p1 through p2 to p3; when the cursor
     * picked the p2 side clockwise, start/end swap so the arc still passes
     * through the middle point.
     */
    const calculateArcFrom3Points = useCallback((
        p1: THREE.Vector3,
        p2: THREE.Vector3,
        p3: THREE.Vector3
    ): ArcData | null => {
        // Find circumcenter of triangle formed by 3 points (XY ground plane)
        const ax = p1.x, ay = p1.y;
        const bx = p2.x, by = p2.y;
        const cx = p3.x, cy = p3.y;

        const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
        if (Math.abs(d) < 0.0001) return null; // Collinear points

        const ux = ((ax * ax + ay * ay) * (by - cy) +
            (bx * bx + by * by) * (cy - ay) +
            (cx * cx + cy * cy) * (ay - by)) / d;
        const uy = ((ax * ax + ay * ay) * (cx - bx) +
            (bx * bx + by * by) * (ax - cx) +
            (cx * cx + cy * cy) * (bx - ax)) / d;

        const center = new THREE.Vector3(ux, uy, p1.z);
        const radius = center.distanceTo(p1);

        // Angles measured from the circumcenter.
        const angOf = (p: THREE.Vector3) => Math.atan2(p.y - uy, p.x - ux);
        let a0 = angOf(p1);
        let aMid = angOf(p2);
        let a1 = angOf(p3);
        // Normalize the CCW sweep a0 -> a1; flip the span direction when the
        // middle point is not inside it (i.e. the user swept clockwise).
        const sweepCCW = (a: number, b: number) => { let s = b - a; while (s <= 0) s += Math.PI * 2; return s; };
        const insideCCW = ((aMid - a0) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
        if (insideCCW > sweepCCW(a0, a1)) {
            const t = a0; a0 = a1; a1 = t;
        }

        return {
            center: [ux, uy, p1.z],
            radius,
            startAngle: a0,
            endAngle: a1,
        };
    }, []);

    /** Center-Start-End solve: radius from center->start, span start->end. */
    const arcFromCSE = useCallback((center: THREE.Vector3, start: THREE.Vector3, end: THREE.Vector3): ArcData | null => {
        const radius = Math.hypot(start.x - center.x, start.y - center.y);
        if (radius < 1e-4) return null;
        const a0 = Math.atan2(start.y - center.y, start.x - center.x);
        const a1 = Math.atan2(end.y - center.y, end.x - center.x);
        return { center: [center.x, center.y, center.z], radius, startAngle: a0, endAngle: a1 };
    }, []);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point, points.length > 0 ? points[points.length - 1] : null);
        const newPoints = [...points, snapped];
        setPoints(newPoints);

        if (cseMode && newPoints.length === 3) {
            const arc = arcFromCSE(newPoints[0], newPoints[1], newPoints[2]);
            if (arc) {
                undoableAdd([makeEntity('arc', `Arc-${Date.now()}`, arc as unknown as Record<string, unknown>, activeLayerId)]);
            }
            setPoints([]);
            setArcPreview(null);
            setCseMode(false);
            return;
        }
        if (!cseMode && newPoints.length === 3) {
            // Complete the 3-point arc (start, point-on-arc, end)
            const arc = calculateArcFrom3Points(newPoints[0], newPoints[1], newPoints[2]);
            if (arc) {
                undoableAdd([makeEntity('arc', `Arc-${Date.now()}`, arc as unknown as Record<string, unknown>, activeLayerId)]);
            }
            setPoints([]);
            setArcPreview(null);
        }
    }, [isActive, points, getGroundPoint, snap, activeLayerId, calculateArcFrom3Points, cseMode, arcFromCSE]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            const snapped = snap(point);
            // Grid-snapped preview: cell-repeat writes skip the setState and
            // the arc re-solve below, so moves inside one cell cost nothing.
            if (previewPoint && previewPoint.distanceToSquared(snapped) < 1e-6) return;
            setPreviewPoint(snapped);

            if (points.length === 2) {
                const arc = calculateArcFrom3Points(points[0], points[1], snapped);
                setArcPreview(arc);
            }
        }
    }, [isActive, points, previewPoint, getGroundPoint, snap, calculateArcFrom3Points]);

    // Cancel on Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isActive) {
                setPoints([]);
                setArcPreview(null);
                setCseMode(false);
            } else if (e.key.toLowerCase() === 'c' && isActive) {
                // ARC options: C toggles the Center-Start-End mode.
                setCseMode((v) => !v);
                setPoints([]);
                setArcPreview(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        points,
        previewPoint,
        arcPreview,
        cseMode,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Arc Preview Component
 */
export const ArcPreview: React.FC<{ arc: ArcData; color?: string; dashed?: boolean; dashSize?: number; gapSize?: number; lineWidth?: number }> = ({ arc, color = '#00ff00', dashed, dashSize = 0.2, gapSize = 0.1, lineWidth = 2 }) => {
    const points = useMemo(() => {
        const pts: THREE.Vector3[] = [];
        const segments = 32;
        const { center, radius, startAngle, endAngle } = arc;

        // let angle = startAngle;
        let angleDiff = endAngle - startAngle;
        if (angleDiff < 0) angleDiff += Math.PI * 2;

        for (let i = 0; i <= segments; i++) {
            const a = startAngle + (angleDiff * i) / segments;
            pts.push(new THREE.Vector3(
                center[0] + Math.cos(a) * radius,
                center[1] + Math.sin(a) * radius,
                center[2]
            ));
        }

        return pts;
    }, [arc]);

    return <Line points={points} color={color} lineWidth={lineWidth} dashed={dashed} dashSize={dashSize} gapSize={gapSize} />;
};

// ============================================================================
// POLYGON TOOL
// ============================================================================

/**
 * Polygon Tool Hook - Regular n-sided polygon
 * 
 * Click 1: Center point
 * Click 2: Vertex point (defines radius)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePolygonTool(sides: number = 6) {
    const [center, setCenter] = useState<THREE.Vector3 | null>(null);
    const [previewRadius, setPreviewRadius] = useState<number>(0);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'polygon';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3, base?: THREE.Vector3 | null): THREE.Vector3 => {
        return constrainDraftPoint(point, camera, gl, base);
    }, [camera, gl]);

    /**
     * Generate polygon vertices
     */
    const generatePolygonVertices = useCallback((
        center: THREE.Vector3,
        radius: number,
        numSides: number
    ): number[][] => {
        const vertices: number[][] = [];
        for (let i = 0; i < numSides; i++) {
            const angle = (i / numSides) * Math.PI * 2 - Math.PI / 2;
            vertices.push([
                center.x + Math.cos(angle) * radius,
                center.y + Math.sin(angle) * radius,
                center.z,
            ]);
        }
        return vertices;
    }, []);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point, center);

        if (!center) {
            setCenter(snapped);
        } else {
            const radius = center.distanceTo(snapped);
            if (radius > 0.1) {
                const vertices = generatePolygonVertices(center, radius, sides);
                undoableAdd([makeEntity('polygon', `Polygon-${sides}-${Date.now()}`, {
                    center: [center.x, center.y, center.z],
                    radius,
                    sides,
                    vertices,
                }, activeLayerId)]);
            }
            setCenter(null);
            setPreviewRadius(0);
        }
    }, [isActive, center, sides, getGroundPoint, snap, activeLayerId, generatePolygonVertices]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !center) return;

        const point = getGroundPoint();
        if (point) {
            const next = center.distanceTo(snap(point));
            setPreviewRadius((prev) => (Math.abs(prev - next) < 1e-4 ? prev : next));
        }
    }, [isActive, center, getGroundPoint, snap]);

    // Cancel on Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isActive) {
                setCenter(null);
                setPreviewRadius(0);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive]);

    return {
        isActive,
        center,
        previewRadius,
        sides,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Polygon Preview Component
 */
export const PolygonPreview: React.FC<{
    center: THREE.Vector3;
    radius: number;
    sides: number;
    color?: string;
    lineWidth?: number;
    dashed?: boolean;
    dashSize?: number;
    gapSize?: number;
}> = ({ center, radius, sides, color = '#00ff00', lineWidth = 2, dashed, dashSize = 0.2, gapSize = 0.1 }) => {
    const points = useMemo(() => {
        const pts: THREE.Vector3[] = [];
        for (let i = 0; i <= sides; i++) {
            const angle = (i / sides) * Math.PI * 2 - Math.PI / 2;
            pts.push(new THREE.Vector3(
                center.x + Math.cos(angle) * radius,
                center.y + Math.sin(angle) * radius,
                center.z
            ));
        }
        return pts;
    }, [center, radius, sides]);

    if (radius < 0.1) return null;

    return (
        <>
            <Line points={points} color={color} lineWidth={lineWidth} dashed={dashed} dashSize={dashSize} gapSize={gapSize} />
            {/* Center line */}
            <Line
                points={[center, points[0]]}
                color="#ffff00"
                lineWidth={1}
                dashed
            />
        </>
    );
};

// ============================================================================
// POLYLINE TOOL
// ============================================================================

/**
 * Polyline Tool Hook - Connected line segments
 * 
 * Click: Add vertex
 * Enter/Double-click: Complete polyline
 * Escape: Cancel
 * C: Close polyline
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePolylineTool() {
    const [vertices, setVertices] = useState<THREE.Vector3[]>([]);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);
    const [isClosed, setIsClosed] = useState(false);
    // Arc mode (PEDIT-style 'A'): after toggling with A, the next click
    // defines the arc midpoint; its bulge is stored for the pending segment.
    const [arcMode, setArcMode] = useState(false);
    const [pendingArcMid, setPendingArcMid] = useState<THREE.Vector3 | null>(null);
    const [bulges, setBulges] = useState<number[]>([]);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'polyline';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3, base?: THREE.Vector3 | null): THREE.Vector3 => {
        return constrainDraftPoint(point, camera, gl, base);
    }, [camera, gl]);

    const completePolyline = useCallback((close: boolean = false) => {
        if (vertices.length >= 2) {
            const hasBulge = bulges.some((b) => b);
            undoableAdd([makeEntity('polyline', `Polyline-${Date.now()}`, {
                vertices: vertices.map(v => [v.x, v.y, v.z]),
                ...(hasBulge ? { bulges } : {}),
                closed: close,
            }, activeLayerId)]);
        }
        setVertices([]);
        setPreviewPoint(null);
        setIsClosed(false);
        setBulges([]);
        setArcMode(false);
        setPendingArcMid(null);
    }, [vertices, bulges, activeLayerId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const base = vertices.length > 0 ? vertices[vertices.length - 1] : null;
        const snapped = snap(point, base);
        if (arcMode && base && pendingArcMid === null) {
            // First click in arc mode = arc midpoint over the pending chord
            // (from the previous vertex toward the cursor's next vertex);
            // store it and wait for the segment end click.
            setPendingArcMid(snapped);
            return;
        }
        if (arcMode && base && pendingArcMid) {
            // Second click = segment end: compute the DXF bulge for
            // base → mid → snapped and commit both the vertex and bulge.
            const bg = bulgeFrom3Points(
                [base.x, base.y, base.z],
                [pendingArcMid.x, pendingArcMid.y, pendingArcMid.z],
                [snapped.x, snapped.y, snapped.z]
            );
            setVertices((prev) => {
                setBulges((bs) => {
                    const next = [...bs];
                    while (next.length < prev.length - 1) next.push(0);
                    next[prev.length - 1] = bg;
                    return next;
                });
                return [...prev, snapped];
            });
            setArcMode(false);
            setPendingArcMid(null);
            return;
        }
        setVertices([...vertices, snapped]);
    }, [isActive, vertices, getGroundPoint, snap, arcMode, pendingArcMid]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            const base = vertices.length > 0 ? vertices[vertices.length - 1] : null;
            const snapped = snap(point, base);
            // Functional dedupe: repeat grid-cell writes keep the old
            // reference so React bails out of the preview render.
            setPreviewPoint((prev) => (prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped));
        }
    }, [isActive, getGroundPoint, snap, vertices]);

    const handleDoubleClick = useCallback(() => {
        if (isActive && vertices.length >= 2) {
            completePolyline(false);
        }
    }, [isActive, vertices.length, completePolyline]);

    // Keyboard handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;

            if (e.key === 'Escape') {
                setVertices([]);
                setPreviewPoint(null);
                setBulges([]);
                setArcMode(false);
                setPendingArcMid(null);
            } else if (e.key === 'Enter') {
                completePolyline(false);
            } else if (e.key.toLowerCase() === 'a' && vertices.length >= 1) {
                // PEDIT-style: A toggles the arc segment mode for the next run.
                setArcMode((v) => !v);
                setPendingArcMid(null);
            } else if (e.key.toLowerCase() === 'c' && vertices.length >= 3) {
                completePolyline(true);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, vertices.length, completePolyline]);

    return {
        isActive,
        vertices,
        previewPoint,
        isClosed,
        arcMode,
        pendingArcMid,
        handlePointerDown,
        handlePointerMove,
        handleDoubleClick,
    };
}

/**
 * Polyline Preview Component
 */
export const PolylinePreview: React.FC<{
    vertices: THREE.Vector3[];
    previewPoint: THREE.Vector3 | null;
    closed?: boolean;
    color?: string;
    lineWidth?: number;
    dashed?: boolean;
    dashSize?: number;
    gapSize?: number;
}> = ({ vertices, previewPoint, closed, color = '#00ff00', lineWidth = 2, dashed, dashSize = 0.2, gapSize = 0.1 }) => {
    const points = useMemo(() => {
        const pts = [...vertices];
        if (previewPoint && pts.length > 0) {
            pts.push(previewPoint);
        }
        if (closed && pts.length >= 3) {
            pts.push(pts[0]);
        }
        return pts;
    }, [vertices, previewPoint, closed]);

    if (points.length < 2) return null;

    return (
        <>
            <Line points={points} color={color} lineWidth={lineWidth} dashed={dashed} dashSize={dashSize} gapSize={gapSize} />
            {/* Vertex markers (only while actively drawing) */}
            {previewPoint && vertices.map((v, i) => (
                <mesh key={i} position={v}>
                    <sphereGeometry args={[0.08, 8, 8]} />
                    <meshBasicMaterial color="#ffff00" />
                </mesh>
            ))}
        </>
    );
};

// ============================================================================
// ELLIPSE TOOL
// ============================================================================

/**
 * Ellipse Tool Hook
 * 
 * Click 1: Center
 * Click 2: First axis end
 * Click 3: Second axis radius
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useEllipseTool() {
    const [center, setCenter] = useState<THREE.Vector3 | null>(null);
    const [axisEnd, setAxisEnd] = useState<THREE.Vector3 | null>(null);
    const [previewPoint, setPreviewPoint] = useState<THREE.Vector3 | null>(null);
    // Elliptical-arc mode (A key): two extra clicks define start/end angle.
    const [arcMode, setArcMode] = useState(false);
    const [arcStart, setArcStart] = useState<THREE.Vector3 | null>(null);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);

    const { raycaster, pointer, camera, gl } = useThree();

    const isActive = activeTool === 'ellipse';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3, base?: THREE.Vector3 | null): THREE.Vector3 => {
        return constrainDraftPoint(point, camera, gl, base);
    }, [camera, gl]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        event.stopPropagation();

        const point = getGroundPoint();
        if (!point) return;

        const snapped = snap(point);

        if (!center) {
            setCenter(snapped);
        } else if (!axisEnd) {
            setAxisEnd(snapped);
        } else if (arcMode && !arcStart) {
            setArcStart(snapped);
        } else {
            // Calculate ellipse parameters (XY ground plane)
            const radiusA = center.distanceTo(axisEnd);
            const angle = Math.atan2(axisEnd.y - center.y, axisEnd.x - center.x);

            // Second radius is perpendicular distance
            const perpDir = new THREE.Vector3(-Math.sin(angle), Math.cos(angle), 0);
            const toPoint = snapped.clone().sub(center);
            const radiusB = Math.abs(toPoint.dot(perpDir));

            if (radiusA > 0.1 && radiusB > 0.1) {
                // Elliptic parameter from a world point: project onto the
                // (a, b) axes — the angle the preview sweeps with.
                const paramOf = (p: THREE.Vector3): number => {
                    const dx = p.x - center.x, dy = p.y - center.y;
                    const ux = dx * Math.cos(angle) + dy * Math.sin(angle);
                    const uy = -dx * Math.sin(angle) + dy * Math.cos(angle);
                    return Math.atan2(uy / radiusB, ux / radiusA);
                };
                const data: Record<string, unknown> = {
                    center: [center.x, center.y, center.z],
                    radiusA,
                    radiusB,
                    rotation: angle,
                };
                if (arcMode && arcStart) {
                    data.startAngle = paramOf(arcStart);
                    data.endAngle = paramOf(snapped);
                }
                undoableAdd([makeEntity('ellipse', `Ellipse-${Date.now()}`, data, activeLayerId)]);
            }

            setCenter(null);
            setAxisEnd(null);
            setPreviewPoint(null);
            setArcMode(false);
            setArcStart(null);
        }
    }, [isActive, center, axisEnd, getGroundPoint, snap, activeLayerId, arcMode, arcStart]);

    const handlePointerMove = useCallback(() => {
        if (!isActive) return;

        const point = getGroundPoint();
        if (point) {
            const snapped = snap(point);
            setPreviewPoint((prev) => (prev && prev.distanceToSquared(snapped) < 1e-6 ? prev : snapped));
        }
    }, [isActive, getGroundPoint, snap]);

    // Cancel on Escape; A toggles elliptical-arc mode
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isActive) return;
            if (e.key === 'Escape') {
                setCenter(null);
                setAxisEnd(null);
                setPreviewPoint(null);
                setArcMode(false);
                setArcStart(null);
            } else if (e.key.toLowerCase() === 'a' && center) {
                setArcMode((v) => !v);
                setArcStart(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, center]);

    return {
        isActive,
        center,
        axisEnd,
        previewPoint,
        arcMode,
        handlePointerDown,
        handlePointerMove,
    };
}

/**
 * Ellipse Preview Component
 */
export const EllipsePreview: React.FC<{
    center: THREE.Vector3;
    radiusA: number;
    radiusB: number;
    rotation?: number;
    color?: string;
    lineWidth?: number;
    dashed?: boolean;
    dashSize?: number;
    gapSize?: number;
    startAngle?: number;
    endAngle?: number;
}> = ({ center, radiusA, radiusB, rotation = 0, color = '#00ff00', lineWidth = 2, dashed, dashSize = 0.2, gapSize = 0.1, startAngle, endAngle }) => {
    const points = useMemo(() => {
        const pts: THREE.Vector3[] = [];
        const segments = 48;
        let t0 = 0, t1 = Math.PI * 2;
        if (startAngle !== undefined && endAngle !== undefined) {
            t0 = startAngle;
            let sweep = endAngle - startAngle;
            while (sweep <= 0) sweep += Math.PI * 2;
            t1 = startAngle + sweep;
        }

        for (let i = 0; i <= segments; i++) {
            const t = t0 + ((t1 - t0) * i) / segments;
            const x = radiusA * Math.cos(t);
            const y = radiusB * Math.sin(t);

            // Rotate in the XY ground plane
            const rx = x * Math.cos(rotation) - y * Math.sin(rotation);
            const ry = x * Math.sin(rotation) + y * Math.cos(rotation);

            pts.push(new THREE.Vector3(
                center.x + rx,
                center.y + ry,
                center.z
            ));
        }

        return pts;
    }, [center, radiusA, radiusB, rotation]);

    if (radiusA < 0.1 || radiusB < 0.1) return null;

    return <Line points={points} color="#00ff00" lineWidth={2} />;
};
